/**
 * "Search All Chats" — channel grouping + deep-link routing ("Conversation misplaced").
 *
 * A DayBlock IS a Conversation, so a Telegram day bucket has always been
 * findable in the global search — but the results were rendered as one flat list
 * and clicking a phone bucket loaded it into the MAIN CHAT, the one surface that
 * is not supposed to render a day bucket at all. This module owns the two
 * decisions that fixes:
 *
 *   1. WHICH header a result belongs under (Main Chat / Telegram), and
 *   2. WHERE clicking it must navigate.
 *
 * The BE already resolves the hard half of (1) — it alone knows the raw `channel`
 * AND whether the caller owns the conversation (a bucket shared WITH you is only
 * readable in the Main Chat) — and ships it as `channel_group`. Everything here is
 * a pure function of the result row, so it is unit-testable and there is exactly
 * one place that maps a group to a route.
 */

// Fixed render order. Main Chat first: it is the user's own surface and the
// overwhelmingly common case, so the phone channels read as the exceptions.
// #530: WhatsApp is hidden in the web app — a legacy row whose channel_group is
// that hidden channel gets no lane of its own; `groupKeyFor` folds it into Main
// Chat, where it opens in place as a normal conversation.
export const CHANNEL_GROUP_ORDER = ['main_chat', 'telegram'];

// i18n key per group (both channel keys already exist — they name the rail
// entries). `null` = no key, use the literal fallback below.
export const CHANNEL_GROUP_I18N = {
    main_chat: 'searchGroupMainChat',
    telegram: 'telegram',
};

export const CHANNEL_GROUP_FALLBACK = {
    main_chat: 'Main Chat',
    telegram: 'Telegram',
};

/**
 * The group a result belongs to.
 *
 * Falls back to 'main_chat' for anything unrecognised — including a payload from
 * an OLDER backend that carries no `channel_group` at all. That fallback is
 * deliberate: a result the user can still open beats a result that silently
 * disappears because its channel is new.
 */
export function groupKeyFor(result) {
    const key = result?.channel_group;
    return CHANNEL_GROUP_ORDER.includes(key) ? key : 'main_chat';
}

/**
 * Turn a FLAT legacy `results[]` into the per-conversation summaries
 * `groupByConversation` consumes (#353 UI refinement).
 *
 * This exists so there is exactly ONE rendering architecture in the sidebar. The
 * old code had two: a grouped path for a backend that ships `conversations[]`, and
 * a flat fallback that rendered every match as a loose row carrying its own
 * conversation title. That fallback IS the "mess" the PO reported — individual
 * messages outside any conversation context — and while it only fires against an
 * older backend, its mere existence is a code path that can put loose messages on
 * screen. Synthesising summaries instead means the fallback renders the same nested
 * shape as everything else, and no branch can regress to loose rows.
 *
 * ⚠️ `occurrences` here is a FLOOR, not a true count: an older backend gives us
 * only the rows it happened to return (a capped page), so we count rows. That is
 * honest — the number cannot exceed reality — where a fabricated total could.
 *
 * Ordering mirrors the BE's: title matches first, then by how many rows we saw.
 *
 * @returns {Array} conversation summaries, same shape as the BE's `conversations[]`
 */
export function synthesizeConversationSummaries(results) {
    const byConv = new Map();
    for (const row of results || []) {
        const cid = row?.conversation_id;
        if (!cid) continue;
        if (!byConv.has(cid)) {
            byConv.set(cid, {
                conversation_id: cid,
                conversation_title: row.conversation_title || 'Untitled',
                occurrences: 0,
                message_matches: 0,
                title_match: false,
                channel: row.channel || 'web',
                channel_group: groupKeyFor(row),
                world: row.world ?? null,
                day_date: row.day_date ?? null,
                updated_at: row.created_at ?? null,
            });
        }
        const conv = byConv.get(cid);
        if (row.result_type === 'title') conv.title_match = true;
        else {
            conv.occurrences += 1;
            conv.message_matches += 1;
        }
    }
    return Array.from(byConv.values()).sort(
        (a, b) => (Number(b.title_match) - Number(a.title_match)) || (b.occurrences - a.occurrences)
    );
}

/**
 * Group the BE's per-conversation summaries into the same channel lanes (#353).
 *
 * The sidebar used to render a flat list of match ROWS, so a term appearing 12
 * times in one chat looked like 12 unrelated results, and the header count was the
 * number of rows the payload happened to carry — capped, and therefore smaller
 * than what the user counts by opening the chats. The BE now returns one entry per
 * conversation with its TRUE `occurrences`; this arranges them for rendering.
 *
 * `total` is the SUM of the occurrence counts in the lane, never the number of
 * conversations — the lane header answers "how many times does this appear on
 * Telegram?", which is the question the user is checking against.
 *
 * The navigation rows (`results`) are attached to their conversation so a click
 * still jumps to a specific message; a conversation whose rows were capped away
 * still renders (with its real count) and opens at the top of the chat.
 *
 * @returns {Array<{key, conversations, total, conversationCount}>}
 */
export function groupByConversation(conversations, results) {
    const rowsByConv = new Map();
    for (const row of results || []) {
        if (row?.result_type === 'title' || !row?.conversation_id) continue;
        if (!rowsByConv.has(row.conversation_id)) rowsByConv.set(row.conversation_id, []);
        rowsByConv.get(row.conversation_id).push(row);
    }

    const lanes = new Map();
    for (const conv of conversations || []) {
        const key = groupKeyFor(conv);
        if (!lanes.has(key)) lanes.set(key, { key, conversations: [], total: 0, conversationCount: 0 });
        const lane = lanes.get(key);
        lane.conversations.push({ ...conv, rows: rowsByConv.get(conv.conversation_id) || [] });
        lane.total += conv.occurrences || 0;
        lane.conversationCount += 1;
    }
    return CHANNEL_GROUP_ORDER.filter((k) => lanes.has(k)).map((k) => lanes.get(k));
}

/** True when the result lives in a channel panel rather than the Main Chat. */
export function isChannelResult(result) {
    return groupKeyFor(result) !== 'main_chat';
}

/**
 * The route that opens this result where it actually lives, or null for a Main
 * Chat result (which keeps the existing in-place conversation load).
 *
 * No `view=expanded` (Telegram dual display #444): the channel panels are
 * standard workspace modules, so a hit opens the bucket in the split slot beside
 * whatever is already open instead of covering it. `day=<conversation id>` is
 * the deep-link the panel
 * consumes — the same one-shot `?param` contract as /boards?board=<id> (#102)
 * and /calendar?focus_event=<id>.
 *
 * `world` rides along for Telegram only (the Dual-Bucket System): the tools and
 * conversations chronologies are separate stores, so the panel must be on the
 * right one before the bucket can be found.
 */
export function channelDeepLink(result, searchQuery = '') {
    const key = groupKeyFor(result);
    if (key === 'main_chat') return null;
    const conversationId = result?.conversation_id;
    if (!conversationId) return null;

    const params = new URLSearchParams();
    params.set('day', conversationId);
    if (key === 'telegram') params.set('world', result?.world || 'tools');
    // A message-level match carries the query so the day opens with its in-day
    // search primed — otherwise a hit buried in a long transcript lands the user
    // at the bottom of the thread with no idea which message matched.
    if (result?.message_id && searchQuery) params.set('q', searchQuery);
    return `/${key}?${params.toString()}`;
}
